import { BackendApiClient } from '../../auth/api/backend-client';
import { BackendValidationError } from '../../auth/api/investor';
import { requireList } from '../../../shared/lib/response-shape';
import type { ScopeContext } from '../../auth/model/types';

const backendClient = new BackendApiClient();

export type ExternalTradeStatus = 'created' | 'processing' | 'success' | 'canceled' | 'failed';

export type ExternalTradeSide = 'buy' | 'sell';

export type ExternalTradeEventType =
  | 'created'
  | 'booked'
  | 'success'
  | 'canceled'
  | 'voided'
  | 'failed';

export const EXTERNAL_TRADE_STATUSES: ExternalTradeStatus[] = [
  'created',
  'processing',
  'success',
  'canceled',
  'failed',
];

export const EXTERNAL_TRADE_SIDES: ExternalTradeSide[] = ['buy', 'sell'];

export type ExternalTradeOrder =
  | 'reference_time'
  | '-reference_time'
  | 'created'
  | '-created';

/**
 * A cancelation is a reversal that has already netted out of the balance, so a blotter
 * asks for every other status rather than for "not canceled" — the backend filters
 * positively, and an absent status filter means no filter at all.
 */
export const BLOTTER_STATUSES: ExternalTradeStatus[] = EXTERNAL_TRADE_STATUSES.filter(
  (status) => status !== 'canceled',
);

/**
 * A trade b-cell-position has accepted but not yet booked. `created` is the row as
 * recorded; `processing` is the ledger leg in flight. Only booking opens a Position and
 * moves the NAV, so a trade in either status is one the figures on screen do not yet
 * reflect — the booking marker counts these to say so.
 */
export const UNBOOKED_TRADE_STATUSES: ExternalTradeStatus[] = ['created', 'processing'];

export interface ExternalTradeQuery {
  portfolioId?: string;
  statuses?: ExternalTradeStatus[];
  orderBy?: ExternalTradeOrder;
}

export interface ExternalTradeReason {
  code: string;
  message: string;
}

export interface ExternalTradeEvent {
  id: string;
  type: ExternalTradeEventType;
  created: string;
  reasons: ExternalTradeReason[];
}

export interface ExternalTradeEventResponse {
  events: ExternalTradeEvent[];
  cursor?: string;
}

export interface ExternalTrade {
  id: string;
  status: string;
  portfolio_id: string;
  account_id: string;
  instrument_id: string;
  instrument_type: string;
  symbol: string;
  side: 'buy' | 'sell';
  symbol_amount: number;
  symbol_scale: number;
  price_amount: number;
  price_scale: number;
  currency: string;
  reference_time: string;
  unique_id: string;
  snapshot_id: string;
  tags: string[];
  created: string;
}

export interface ExternalTradeResponse {
  trades: ExternalTrade[];
  cursor?: string;
}

export interface CreateExternalTrade {
  portfolio_id: string;
  instrument_id: string;
  side: 'buy' | 'sell';
  symbol_amount: number;
  symbol_scale: number;
  price_amount: number;
  price_scale: number;
  reference_time: string;
  unique_id?: string;
  tags?: string[];
}

export async function getExternalTrades(
  sessionId: string,
  scopeContext: ScopeContext,
  query: ExternalTradeQuery = {},
): Promise<ExternalTradeResponse> {
  const params = new URLSearchParams();
  if (query.portfolioId) params.set('portfolio_id', query.portfolioId);
  if (query.statuses?.length) params.set('status', query.statuses.join(','));
  if (query.orderBy) params.set('order_by', query.orderBy);
  const search = params.toString();
  const response = await backendClient.get(
    `/external-trade${search ? `?${search}` : ''}`,
    sessionId,
    scopeContext,
  );
  if (response.success && response.data) {
    const body = response.data as ExternalTradeResponse;
    return {
      ...body,
      trades: requireList<ExternalTrade>(body, 'trades', 'Unreadable external trade response'),
    };
  }
  if (response.validationError) {
    throw response.validationError;
  }
  throw new BackendValidationError(response.error || 'Failed to fetch external trades');
}

export async function getExternalTradeEvents(
  sessionId: string,
  scopeContext: ScopeContext,
  params: { tradeIds: string[] },
): Promise<ExternalTradeEventResponse> {
  const query = new URLSearchParams();
  query.set('trade_ids', params.tradeIds.join(','));
  const response = await backendClient.get(
    `/external-trade/event?${query}`,
    sessionId,
    scopeContext,
  );
  if (response.success && response.data) {
    const body = response.data as ExternalTradeEventResponse;
    return {
      ...body,
      events: requireList<ExternalTradeEvent>(body, 'events', 'Unreadable trade event response'),
    };
  }
  if (response.validationError) {
    throw response.validationError;
  }
  throw new BackendValidationError(response.error || 'Failed to fetch trade events');
}

export async function createExternalTrades(
  sessionId: string,
  scopeContext: ScopeContext,
  trades: CreateExternalTrade[],
): Promise<ExternalTradeResponse> {
  const response = await backendClient.post('/external-trade', sessionId, { trades }, scopeContext);
  if (response.success && response.data) {
    const body = response.data as ExternalTradeResponse;
    return {
      ...body,
      trades: requireList<ExternalTrade>(body, 'trades', 'Unreadable external trade response'),
    };
  }
  if (response.validationError) {
    throw response.validationError;
  }
  throw new BackendValidationError(response.error || 'Failed to record external trades');
}
